import React from "react";

/* Why this exists.

   Every deploy gives the content-hashed chunks new filenames, and Azure Static
   Web Apps then serves only the new set. Anyone who had a page open across a
   deploy is holding an index.html that points at filenames which no longer
   exist, so the first code-split route they navigate to rejects its import().

   With 173 lazy routes, `Suspense fallback={null}`, and no real error boundary,
   that rejection threw all the way to the root and React unmounted the entire
   tree. The result was a completely blank white page — no message, nothing to
   click, no way back — and it landed on "Start free", the primary CTA, for
   exactly the people who had been browsing when we shipped.

   The previous guard could not have caught it. It was a function component
   wrapping `children` in a try/catch, which never fires: children are already
   -constructed elements, and their errors are raised later inside React's
   render phase, not inside that try block. Only a class component with
   getDerivedStateFromError sees a descendant's render error. */

const RELOAD_FLAG = "sift_chunk_reload";

// No browser standardizes this wording, so match the shapes that actually turn
// up rather than one exact string. Chrome, Firefox and Safari each phrase a
// failed dynamic import differently.
function isStaleChunk(error) {
  const msg = `${error?.name || ""} ${error?.message || ""}`;
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error, reloading: isStaleChunk(error) };
  }

  componentDidMount() {
    // Reaching mount means this boot is on assets that actually load, so the
    // guard is spent and a future deploy gets its own single retry.
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* private mode; the flag just doesn't persist */
    }
  }

  componentDidCatch(error) {
    if (!isStaleChunk(error)) {
      console.error(error);
      return;
    }
    // Exactly one reload. If the freshly-fetched HTML still can't load the
    // chunk then staleness wasn't the problem, and a reload loop would be a
    // worse failure than telling them plainly.
    let already = false;
    try {
      already = sessionStorage.getItem(RELOAD_FLAG) === "1";
    } catch {
      /* noop */
    }
    if (already) {
      this.setState({ reloading: false });
      return;
    }
    try {
      sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch {
      /* noop */
    }
    window.location.reload();
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;

    // A reload is already in flight; render nothing rather than flashing an
    // error the visitor will never finish reading.
    if (reloading) return null;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          textAlign: "center",
          background: "var(--bg, #FAF8F2)",
          color: "var(--ink, #14120E)",
          fontFamily: "var(--font-sans, system-ui, sans-serif)",
        }}
      >
        <div style={{ maxWidth: 380 }}>
          <h1 style={{ fontSize: "1.375rem", fontWeight: 700, margin: 0 }}>This page didn't load</h1>
          <p style={{ marginTop: 10, fontSize: "0.9375rem", color: "var(--text-secondary, #5b5750)" }}>
            Something went wrong on our end. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              background: "var(--brand, #2F6F4E)",
              color: "#fff",
              border: 0,
              padding: "13px 30px",
              borderRadius: 100,
              fontWeight: 600,
              fontSize: "1rem",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Reload
          </button>
          <div style={{ marginTop: 14 }}>
            <a href="/" style={{ fontSize: "0.875rem", color: "var(--text-secondary, #5b5750)" }}>
              Go to the homepage
            </a>
          </div>
        </div>
      </div>
    );
  }
}
